import { Button, Card, Flex, Form, Space } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  useCreateCourseMutation,
  useGetCoursesQuery,
  useGetSingleCourseQuery,
  useUpdateCourseMutation,
} from "../../../app/features/admin/admin.api";
import type {
  CreateCourseDto,
  UpdateCourseDto,
} from "../../../app/features/admin/admin.types";
import DashboardLayout from "../../../layouts/DashboardLayout";
import { toast } from "../../../lib/toast";
import { slug } from "../../../utils/string.types";
import GeneralCourseInfo from "./_components/ManageCourse/GeneralCourseInfo";
import PreviewCourse from "./_components/ManageCourse/PreviewCourse";
import TitleProgressbar from "./_components/TitleProgressbar";

export default function ManageCourse() {
  const [step, setStep] = useState(0);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { courseId } = useParams();
  const courseIdNum = courseId ? Number(courseId) : undefined;
  const isEditMode = !!courseIdNum;

  const [status, setStatus] = useState<"DRAFT" | "PUBLISHED">("DRAFT");
  const [createCourse, { isLoading: isCreating }] = useCreateCourseMutation();
  const [updateCourse, { isLoading: isUpdating }] = useUpdateCourseMutation();
  const { data: courses, refetch } = useGetCoursesQuery();

  // Fetch course data if editing
  const { data: courseData, isLoading: isFetchingCourse } =
    useGetSingleCourseQuery(courseIdNum!, {
      skip: !courseIdNum,
    });

  // Pre-populate form when editing
  useEffect(() => {
    if (courseData && isEditMode) {
      form.setFieldsValue({
        title: courseData.title,
        slug: courseData.slug,
        shortDescription: courseData.shortDescription,
        description: courseData.description,
        durationLabel: courseData.durationLabel,
        isCertificateEnabled: courseData.isCertificateEnabled,
        language: courseData.language,
        level: courseData.level,
        thumbnailUrl: courseData.thumbnailUrl,
        bannerUrl: courseData.bannerUrl,
        price: Number(courseData.price),
        offerPrice: Number(courseData.offerPrice),
        categoryId: courseData.categoryId,
        subCategoryId: courseData.subCategoryId,
        visibility: courseData.visibility,
        isFeatured: courseData.isFeatured,
      });
    }
  }, [courseData, isEditMode, form]);

  const onFinish = async () => {
    // This will be called when Publish Course is clicked
    await handleSubmit("PUBLISHED");
  };

  const handleSubmit = async (status: "DRAFT" | "PUBLISHED") => {
    setStatus(status);
    try {
      // Validate form
      const values = await form.validateFields();

      if (isEditMode && courseIdNum) {
        // UPDATE MODE
        const updateData: UpdateCourseDto = {
          title: values.title,
          slug: courseData?.slug || slug(values.title),
          shortDescription: values.shortDescription,
          description: values.description || "",
          durationLabel: values.durationLabel,
          isCertificateEnabled: values.isCertificateEnabled || false,
          language: values.language,
          level: values.level,
          thumbnailUrl: values.thumbnailUrl || "",
          bannerUrl: values.bannerUrl || "",
          price: Number(values.price),
          offerPrice: Number(values.offerPrice || 0),
          categoryId: values.categoryId,
          subCategoryId: values.subCategoryId || null,
          visibility: "PUBLIC",
          status,
          isFeatured: values.isFeatured || false,
        };

        await updateCourse({ id: courseIdNum, ...updateData }).unwrap();

        toast(
          "success",
          status === "DRAFT"
            ? "Course updated as draft successfully"
            : "Course updated successfully",
        );

        navigate(`/admin/my-course/manage-course/highlights/${courseIdNum}`);
      } else {
        // CREATE MODE
        // Generate UUID for new course
        const uuid = crypto.randomUUID();

        // Transform form values to match CreateCourseDto
        const courseData: CreateCourseDto = {
          uuid,
          title: values.title,
          slug: slug(values.title) || "",
          shortDescription: values.shortDescription,
          description: values.description || "",
          durationLabel: values.durationLabel,
          isCertificateEnabled: values.isCertificateEnabled || false,
          language: values.language,
          level: values.level,
          thumbnailUrl: values.thumbnailUrl || "",
          bannerUrl: values.bannerUrl || "",
          price: Number(values.price),
          offerPrice: Number(values.offerPrice || 0),
          categoryId: values.categoryId,
          subCategoryId: values.subCategoryId || null,
          visibility: "PUBLIC",
          status,
          isFeatured: values.isFeatured || false,
        };

        // Create course
        const course = await createCourse(courseData).unwrap();

        toast(
          "success",
          status === "DRAFT"
            ? "Course saved as draft successfully"
            : "Course published successfully",
        );

        // Refetch courses to get updated list
        await refetch();

        // Try multiple approaches to get the course ID
        let courseId = course?.id;

        // If no ID in response, try finding it in the refetched list
        if (!courseId) {
          const findCourse = courses?.find(
            (el) => el?.uuid === (course?.uuid || uuid),
          );

          if (findCourse?.id) courseId = findCourse?.id;
        }

        // Navigate if we have an ID, otherwise use UUID
        if (status === "DRAFT") {
          navigate(`/admin/my-course`);
        } else if (courseId) {
          navigate(`/admin/my-course/manage-course/highlights/${courseId}`);
        } else {
          console.error("No course ID or UUID found");
          toast(
            "error",
            "Course created but navigation failed. Please refresh and try again.",
          );
          return;
        }

        setStep(step + 1);
      }
    } catch (error: any) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} course:`,
        error,
      );
      toast(
        "error",
        error?.data?.message ||
          `Failed to ${isEditMode ? "update" : "create"} course`,
      );
    }
  };

  const isLoading = isCreating || isUpdating;

  return (
    <>
      <DashboardLayout title="Dashboard">
        <TitleProgressbar step={0} />

        {isFetchingCourse ? (
          <Card className="rounded-md shadow-sm mt-5">
            <div className="text-center py-8 text-gray-400">
              Loading course data...
            </div>
          </Card>
        ) : (
          <>
            {/* Form */}
            <Form
              form={form}
              layout="vertical"
              className="custom-form"
              onFinish={onFinish}
              initialValues={{
                visibility: "PUBLIC",
                isCertificateEnabled: false,
                isFeatured: false,
                sections: [
                  {
                    sectionName: "",
                    lectures: [
                      {
                        lectureTitle: "",
                        type: undefined,
                        allowFreePreview: false,
                      },
                    ],
                  },
                ],
              }}
            >
              {step === 0 && (
                <Card
                  className="rounded-md shadow-sm mt-5"
                  bodyStyle={{ padding: "22px" }}
                >
                  <GeneralCourseInfo form={form} />
                </Card>
              )}

              {step === 1 && <PreviewCourse form={form} />}

              {/* Footer Buttons */}
              <Flex justify="space-between" align="center" className="mt-5">
                <Button
                  type="default"
                  size="large"
                  className="btn-bordered-primary border border-zinc-200 hover:!border-zinc-200 focus:!border-zinc-200 active:!border-zinc-200"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>

                <Space>
                  {courseData?.status !== "PUBLISHED" && (
                    <Button
                      type="default"
                      size="large"
                      className="btn-bordered-primary border border-zinc-200 hover:!border-zinc-200 focus:!border-zinc-200 active:!border-zinc-200"
                      onClick={() => handleSubmit("DRAFT")}
                      disabled={isLoading}
                      loading={isLoading && status === "DRAFT"}
                    >
                      Save as Draft
                    </Button>
                  )}

                  <Button
                    type="primary"
                    size="large"
                    className="shadow-sm"
                    disabled={isLoading}
                    htmlType="submit"
                    loading={isLoading && status === "PUBLISHED"}
                  >
                    {isEditMode ? "Update Course" : "Continue"}
                  </Button>
                </Space>
              </Flex>
            </Form>
          </>
        )}
      </DashboardLayout>
    </>
  );
}
